import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVoiceSession } from './VoiceSessionContext';
import { motion, AnimatePresence } from 'framer-motion';

export default function FloatingVoiceBanner() {
  const { session, isMuted, toggleMute, endSession, speakingUsers } = useVoiceSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [radioMuted, setRadioMuted] = useState(false);

  const toggleRadioMute = useCallback(() => {
    const newMuted = !radioMuted;
    setRadioMuted(newMuted);
    // Mute/unmute all radio audio elements in the DOM
    document.querySelectorAll('[id^="radio-audio-"]').forEach(el => { el.muted = newMuted; });
  }, [radioMuted]);

  if (!session) return null;

  // Don't show banner if already on the voice page
  const sessionPath =
    session.type === 'voice-room' ? `/voice-rooms/${session.id}` :
    session.type === 'chat-call' ? `/chat/${session.id}` :
    session.type === 'open-chat' ? `/open-chat/${session.id}` :
    session.type === 'radio-host' ? '/radio/broadcast' :
    session.type === 'radio' ? `/radio/${session.id}` : null;

  if (sessionPath && location.pathname === sessionPath) return null;

  const typeLabel =
    session.type === 'voice-room' ? '🎙️ 보이스룸' :
    session.type === 'chat-call' ? '📞 통화 중' :
    session.type === 'open-chat' ? '🎤 음성 채팅' :
    session.type === 'radio-host' ? '📻 방송 중' :
    session.type === 'radio' ? '📻 라디오' : '🔊 음성';

  // Radio listeners use volume mute; hosts use mic mute
  const isRadioListener = session.type === 'radio';
  const isRadioHost = session.type === 'radio-host';
  const isRadio = isRadioListener || isRadioHost;

  const activeSpeakers = speakingUsers.size;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        className="fixed left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-md"
        style={{ bottom: 'max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}
      >
        <div
          className="relative rounded-2xl px-3 py-2.5 flex items-center gap-2 cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, rgba(124,58,237,0.3), rgba(244,114,182,0.2))',
            backdropFilter: 'blur(20px) saturate(1.8)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.8)',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 8px 32px rgba(124,58,237,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
          }}
          onClick={() => sessionPath && navigate(sessionPath)}
        >
          {/* Animated sound wave indicator */}
          <div className="flex items-center gap-0.5 shrink-0">
            {[1,2,3].map(i => (
              <motion.div
                key={i}
                className="w-[3px] rounded-full bg-green-400"
                animate={{
                  height: activeSpeakers > 0 ? [4, 12 + Math.random()*8, 4] : [4, 6, 4],
                }}
                transition={{ repeat: Infinity, duration: 0.5 + i*0.1, ease: 'easeInOut' }}
              />
            ))}
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-white text-xs font-medium truncate">{typeLabel}</div>
            <div className="text-white/60 text-[11px] truncate">{session.title}</div>
          </div>

          {/* Mute button */}
          <button
            onClick={(e) => { e.stopPropagation(); isRadioListener ? toggleRadioMute() : toggleMute(); }}
            className={`w-10 h-10 min-w-[40px] rounded-full flex items-center justify-center text-base transition shrink-0 ${
              (isRadioListener ? radioMuted : isMuted) ? 'bg-red-500/30 text-red-400' : 'bg-white/10 text-white'
            }`}
          >
            {isRadioListener ? (radioMuted ? '🔇' : '🔊') : (isMuted ? '🔇' : '🎤')}
          </button>

          {/* End button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isRadio) {
                if (!window.confirm(isRadioHost ? '방송을 종료하시겠습니까?' : '라디오 청취를 종료하시겠습니까?')) return;
              }
              endSession();
            }}
            className="w-10 h-10 min-w-[40px] rounded-full bg-red-500/30 text-red-400 flex items-center justify-center text-base transition shrink-0 hover:bg-red-500/50"
          >
            ✕
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
